// Read-time normalization, per item. Each `normalize<Type>V<N>` step fills the
// defaults that version N introduced for that type, idempotently and
// conditioned on field presence — never on the stored `version` number. So a
// blob written by an older or future client passes through cleanly.
//
// The chain is registered in `PATTERN_NORMALIZERS` / `SESSION_NORMALIZERS`,
// indexed by version. `normalize<Type>` runs every step from 1 up to
// `CURRENT_<TYPE>_VERSION`. Invariants enforced by tests:
//   - Every version 1..CURRENT has a registered normalizer.
//   - There are no extra normalizers beyond CURRENT.
//
// When extending a per-item schema with a new field:
//   1. Add the field as **optional** in `schemas.ts` (additive only).
//   2. Bump `CURRENT_PATTERN_VERSION` or `CURRENT_SESSION_VERSION`.
//   3. Add a `normalize<Type>V<N>` here (it can be identity if there is no
//      default to backfill) and register it in the table below.
//   4. Re-read `docs/architecture/storage-versioning.md` if you're unsure
//      whether the change is actually additive — it almost always should be.
//
// Breaking changes (renames, type swaps, semantic shifts) are out of scope
// for this file: per-item parse-or-preserve handles them by quarantining the
// item, not by transforming it.

import { CURRENT_PATTERN_VERSION, CURRENT_SESSION_VERSION } from '../types/state';
import { track } from '../analytics/posthog';

function isObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

// ─── Pattern normalizers ──────────────────────────────────────────────────

/** v1 is the initial pattern shape; no defaults to fill yet. */
function normalizePatternV1(raw: unknown): unknown {
  return raw;
}

/**
 * v2 introduced optional `Pattern.js_source` for the JS authoring path. No
 * defaults to backfill — patterns without it are DSL-authored and behave as
 * today.
 */
function normalizePatternV2(raw: unknown): unknown {
  return raw;
}

/**
 * v3 introduced optional `Pattern.color` for the activity calendar's
 * dominant-color bar. No defaults to backfill — absent patterns are colored
 * via deriveColor(id) at render time.
 */
function normalizePatternV3(raw: unknown): unknown {
  return raw;
}

export const PATTERN_NORMALIZERS: Readonly<Record<number, (raw: unknown) => unknown>> = {
  1: normalizePatternV1,
  2: normalizePatternV2,
  3: normalizePatternV3,
};

export function normalizePattern(raw: unknown): unknown {
  if (!isObject(raw)) return raw;
  const storedVersion = typeof raw.version === 'number' ? raw.version : 0;
  let next: unknown = raw;
  for (let v = 1; v <= CURRENT_PATTERN_VERSION; v++) {
    const fn = PATTERN_NORMALIZERS[v];
    if (!fn) throw new Error(`Missing normalizePatternV${v} in dispatch chain`);
    next = fn(next);
  }
  if (storedVersion < CURRENT_PATTERN_VERSION) {
    track('migration_applied', {
      kind: 'pattern',
      from_version: storedVersion,
      to_version: CURRENT_PATTERN_VERSION,
    });
  }
  return next;
}

// ─── Session normalizers ──────────────────────────────────────────────────

/**
 * v1 introduced `Session.title` as a required field. Backfill it from the
 * linked pattern's title (when known), or a sentinel, on any session that's
 * missing one.
 */
function normalizeSessionV1(raw: unknown, patternTitles: Record<string, string>): unknown {
  if (!isObject(raw)) return raw;
  if (typeof raw.title === 'string') return raw;
  const patternId = typeof raw.patternId === 'string' ? raw.patternId : '';
  return { ...raw, title: patternTitles[patternId] ?? 'Untitled project' };
}

/**
 * v2 introduced optional `Session.paramValues`. No defaults to backfill —
 * sessions that omit it simply have no overrides and the engine falls back
 * to authored defaults at calculateIRState entry.
 */
function normalizeSessionV2(raw: unknown, _patternTitles: Record<string, string>): unknown {
  return raw;
}

/**
 * v3 introduced optional `Session.compiledDsl` for JS-authored patterns
 * started with non-default params. No defaults to backfill.
 */
function normalizeSessionV3(raw: unknown, _patternTitles: Record<string, string>): unknown {
  return raw;
}

/**
 * v4 introduced optional `Session.metadata` for project-type SVG mapping and
 * other schemaless tags. No defaults to backfill — sessions without metadata
 * read back with the field absent.
 */
function normalizeSessionV4(raw: unknown, _patternTitles: Record<string, string>): unknown {
  return raw;
}

export const SESSION_NORMALIZERS: Readonly<
  Record<number, (raw: unknown, patternTitles: Record<string, string>) => unknown>
> = {
  1: normalizeSessionV1,
  2: normalizeSessionV2,
  3: normalizeSessionV3,
  4: normalizeSessionV4,
};

export function normalizeSession(raw: unknown, patternTitles: Record<string, string>): unknown {
  if (!isObject(raw)) return raw;
  const storedVersion = typeof raw.version === 'number' ? raw.version : 0;
  let next: unknown = raw;
  for (let v = 1; v <= CURRENT_SESSION_VERSION; v++) {
    const fn = SESSION_NORMALIZERS[v];
    if (!fn) throw new Error(`Missing normalizeSessionV${v} in dispatch chain`);
    next = fn(next, patternTitles);
  }
  if (storedVersion < CURRENT_SESSION_VERSION) {
    track('migration_applied', {
      kind: 'session',
      from_version: storedVersion,
      to_version: CURRENT_SESSION_VERSION,
    });
  }
  return next;
}

// ─── Patterns lookup helper ───────────────────────────────────────────────

/**
 * Build a `patternId -> title` lookup from a raw patterns record. Tolerates
 * any shape — entries without a string title contribute nothing — so it can
 * run before the per-pattern safeParse step.
 */
export function patternTitlesFromRaw(rawPatterns: unknown): Record<string, string> {
  if (!isObject(rawPatterns)) return {};
  const out: Record<string, string> = {};
  for (const [id, p] of Object.entries(rawPatterns)) {
    if (isObject(p) && typeof p.title === 'string') out[id] = p.title;
  }
  return out;
}
